/**
 * Typed batch API helpers — Track 2.G.
 *
 * Thin wrappers around `ApiClient` for batch operations.
 *
 * Batch URL routing (cook-as-process-design §3.6):
 *   /batch/<uuid>                 — 301 → /r/<primary>/batch/<uuid> if primary set
 *   /r/<recipe-uuid>/batch/<uuid> — canonical URL for single-primary batches
 *
 * Warning-modal flow for closing with in-progress children (§3.4):
 *   1. Call closeBatch({ uuid, force: false })
 *   2. On CONFLICT("open_in_progress_children"): show modal with count
 *   3. User confirms: call closeBatch({ uuid, force: true })
 *
 * Architecture refs:
 *   functionality.md §3 — batch domain model (lines 125–140)
 *   cook-as-process-design §3 — batch status machine + URL routing
 *   architecture.md §4.7 — DDL: batches, batch_recipes
 *   architecture.md §4.23 — server-stamped author_agent; NOT accepted from client
 */

import type { ApiClient } from "./index.js";

// ── Row types ─────────────────────────────────────────────────────────────────

export interface BatchRow {
  uuid: string;
  slug: string;
  name: string;
  /** nullable; primary recipe for Explorer location + URL routing. */
  recipe_uuid: string | null;
  body_markdown: string;
  /** 'open' | 'closed' | 'archived' */
  status: string;
  batch_kind_uuid: string | null;
  created_at: number;
  updated_at: number;
  closed_at: number | null;
}

// ── Input types ───────────────────────────────────────────────────────────────

export interface BatchCreateInput {
  slug: string;
  name: string;
  primaryRecipeUuid?: string | null;
  bodyMarkdown?: string;
}

export interface BatchUpdateInput {
  uuid: string;
  name?: string;
  slug?: string;
  bodyMarkdown?: string;
  primaryRecipeUuid?: string | null;
}

export interface BatchListInput {
  recipeUuid?: string | null;
  status?: "open" | "closed" | "archived" | null;
  limit?: number;
  cursor?: string | null;
}

export interface BatchCloseInput {
  uuid: string;
  /**
   * When false (default), the server returns CONFLICT if any child attempts
   * are still in-progress. Client should surface the warning modal, then
   * retry with force = true.
   */
  force?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Create a new batch.
 *
 * `primaryRecipeUuid` is nullable — multi-recipe batches have no primary.
 * The batch starts in 'open' status.
 *
 * @throws `ORPCError("CONFLICT")` if slug is taken.
 * @throws `ORPCError("NOT_FOUND")` if primaryRecipeUuid does not exist.
 */
export async function createBatch(api: ApiClient, input: BatchCreateInput): Promise<BatchRow> {
  return (await api.batch.create(input)) as BatchRow;
}

/**
 * Fetch a single batch by UUID.
 */
export async function getBatch(api: ApiClient, uuid: string): Promise<BatchRow> {
  return (await api.batch.get({ uuid })) as BatchRow;
}

/**
 * List batches with optional recipe/status filter and keyset pagination.
 */
export async function listBatches(
  api: ApiClient,
  input: BatchListInput = {},
): Promise<{ batches: BatchRow[]; nextCursor: string | null }> {
  return (await api.batch.list(input)) as {
    batches: BatchRow[];
    nextCursor: string | null;
  };
}

/**
 * Update a batch's name, slug, body, or primary recipe.
 * Only open/archived batches can be updated.
 */
export async function updateBatch(api: ApiClient, input: BatchUpdateInput): Promise<BatchRow> {
  return (await api.batch.update(input)) as BatchRow;
}

/**
 * Close a batch (open → closed).
 *
 * The server checks for in-progress child attempts and returns
 * `ORPCError("CONFLICT", { message: "open_in_progress_children", data: { inProgressCount: N } })`
 * when `force = false` (default). Surface the warning modal to the user, then
 * call again with `force = true` to confirm.
 *
 * Closing does NOT auto-transition child attempts.
 *
 * @throws `ORPCError("CONFLICT")` when children are in-progress (force = false) or batch is not open.
 */
export async function closeBatch(api: ApiClient, input: BatchCloseInput): Promise<BatchRow> {
  return (await api.batch.close(input)) as BatchRow;
}

/**
 * Reopen a batch (closed → open).
 * The sync-DO allocates a fresh y-doc under a new reopen-nonce key.
 */
export async function reopenBatch(api: ApiClient, uuid: string): Promise<BatchRow> {
  return (await api.batch.reopen({ uuid })) as BatchRow;
}

/**
 * Archive a batch.
 */
export async function archiveBatch(api: ApiClient, uuid: string): Promise<BatchRow> {
  return (await api.batch.archive({ uuid })) as BatchRow;
}

/**
 * Hard-delete a batch.
 */
export async function deleteBatch(api: ApiClient, uuid: string): Promise<{ ok: boolean }> {
  return (await api.batch.delete({ uuid })) as { ok: boolean };
}

/**
 * Resolve the URL routing for /batch/<uuid>.
 *
 * Returns `{ batch, redirectUrl }`:
 *   - `redirectUrl` is set to `/r/<primary>/batch/<uuid>` when the batch has
 *     a primary_recipe_uuid.
 *   - `redirectUrl` is null for multi-recipe batches (render at top-level).
 *
 * The caller (router Worker or server-side handler) issues the 301.
 */
export async function resolveBatchUrl(
  api: ApiClient,
  uuid: string,
): Promise<{ batch: BatchRow; redirectUrl: string | null }> {
  return (await api.batch.resolveUrl({ uuid })) as {
    batch: BatchRow;
    redirectUrl: string | null;
  };
}

/**
 * Add a recipe to the batch_recipes join (idempotent).
 */
export async function addBatchRecipe(
  api: ApiClient,
  batchUuid: string,
  recipeUuid: string,
): Promise<{ ok: boolean }> {
  return (await api.batch.addRecipe({ batchUuid, recipeUuid })) as {
    ok: boolean;
  };
}

/**
 * List recipe UUIDs that are part of this batch.
 */
export async function listBatchRecipes(
  api: ApiClient,
  batchUuid: string,
): Promise<{ recipeUuids: string[] }> {
  return (await api.batch.listRecipes({ uuid: batchUuid })) as {
    recipeUuids: string[];
  };
}
